import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import MailIcon from "@redotech/redo-web/arbiter-icon/mail-01.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Footer } from "./footer";
import { Header } from "./header";
import { SocialLoginOptionsSection } from "./social-login-options-section";
import { useOneTimePasscode } from "./use-one-time-passcode";

type OneTimePasscodeProps = {
  giftButtonClicked?: () => void;
  zipcodeLoginClicked?: () => void;
  registerButtonClicked?: () => void;
  title?: string;
  showFooter?: boolean;
  showLogo?: boolean;
  nextUrl?: string;
  onBack?: () => void;
};

export function OneTimePasscode({
  giftButtonClicked,
  zipcodeLoginClicked,
  registerButtonClicked,
  onBack: onBackProp,
  title,
  showFooter,
  nextUrl,
  showLogo,
}: OneTimePasscodeProps) {
  const {
    texts,
    emailInput,
    onEmailChange,
    onSendLink,
    sendLinkDisabled,
    emailValidationError,
    resendEmailError,
    sendEmailError,
    logo,
    showEmailConfirmationPage,
    canResend,
    onResendLink,
    hideGiftOption,
    registrationEnabled,
    socialLoginOptions,
    onBack,
  } = useOneTimePasscode({ onBack: onBackProp, nextUrl, title });

  const errorUI =
    emailValidationError || sendEmailError || resendEmailError ? (
      <Flex align="center" flexDirection="column">
        {emailValidationError && (
          <Text fontSize="xxs" textAlign="center" textColor="error">
            {emailValidationError}
          </Text>
        )}
        {sendEmailError && (
          <Text fontSize="xxs" textAlign="center" textColor="error">
            {sendEmailError}
          </Text>
        )}
        {resendEmailError && (
          <Text fontSize="xxs" textAlign="center" textColor="error">
            {resendEmailError}
          </Text>
        )}
      </Flex>
    ) : null;

  const loginOptionsUI = (
    <Flex align="center" flexDirection="column" gap="md" justify="flex-start">
      {zipcodeLoginClicked && (
        <RedoButton
          hierarchy="link_underline"
          onClick={zipcodeLoginClicked}
          text={texts.dontHaveEmail}
        />
      )}
      {!hideGiftOption && giftButtonClicked && (
        <RedoButton
          hierarchy="link_underline"
          onClick={giftButtonClicked}
          text={texts.giftButton}
        />
      )}
      {registrationEnabled && registerButtonClicked && (
        <RedoButton
          hierarchy="link_underline"
          onClick={registerButtonClicked}
          text={texts.registerButton}
        />
      )}
    </Flex>
  );

  const sendEmailUI = (
    <Flex align="stretch" flexDirection="column" gap="3xl">
      <Header
        leftAlign={!showLogo}
        logo={showLogo ? logo : undefined}
        merchantName={texts.merchantName}
        onBack={onBack}
        subtitle={texts.subtitle}
        title={texts.title}
      />
      <Flex
        align="stretch"
        flex={1}
        flexDirection="column"
        gap="2xl"
        grow={1}
        justify="flex-start"
      >
        <RedoTextInput
          fullWidth
          label={texts.emailLabel}
          placeholder={texts.emailHint}
          required
          setValue={onEmailChange}
          size="md"
          value={emailInput}
        />
        <RedoButton
          disabled={sendLinkDisabled}
          hierarchy="primary"
          onClick={() => onSendLink()}
          size="lg"
          text={texts.sendLink}
          theme="normal"
        />
        {errorUI}
      </Flex>
      {socialLoginOptions && socialLoginOptions.length > 0 && (
        <SocialLoginOptionsSection
          nextUrl={nextUrl}
          socialLoginOptions={socialLoginOptions}
        />
      )}
      {loginOptionsUI}
      {showFooter && <Footer centered />}
    </Flex>
  );

  const emailConfirmationUI = (
    <Flex flexDirection="column" gap="3xl">
      <RedoFeaturedIcon color="black" Icon={MailIcon} size="xl" />
      <Flex flexDirection="column" gap="lg" maxW="xs">
        <Text fontSize="xl" fontWeight="semibold">
          {texts.emailConfirmationTitle}
        </Text>
        <Text fontSize="sm">{texts.emailConfirmationSubtitle}</Text>
      </Flex>
      <Flex align="center" flexDirection="row" gap="sm">
        <Text fontSize="sm">{texts.didNotReceiveEmail}</Text>
        <RedoButton
          disabled={!canResend}
          hierarchy={canResend ? "link_underline" : "link"}
          onClick={() => onResendLink()}
          text={texts.resendLink}
        />
      </Flex>
      {errorUI}
    </Flex>
  );

  return showEmailConfirmationPage ? emailConfirmationUI : sendEmailUI;
}
